import type { BottleneckLadderRead } from '@/versions/v1/schema/capacity';
import { formatCurrency, recoveryCapPhrase } from './Scene.transform';

export type BottleneckLadderProjection = {
  currentRate: string;
  recoveryFloor: string;
  /** True when D18 caps the feed below the measured rate at this grade. */
  feedLimited: boolean;
  rungs: readonly {
    id: string;
    name: string;
    /**
     * The rung's own constraint, named once (D22): its circuit, or
     * "Feed limited · <circuit>" on the rung that caps the feed today.
     */
    label: string;
    /** Operating (no capital) or capital; none on the feed-limiting rung, whose label says it. */
    pill: 'Operating' | 'Capital required' | 'Capital' | null;
    /** "79.2 stph · +7.5" — the ceiling and the rate gained over the preceding step. */
    rateLine: string;
    /** The step's money: its gain over the preceding step, or today's EBITDA on the feed-limiting rung. */
    money: string;
    moneyTone: 'ok' | 'bad' | 'neutral';
    /** D23 caps that bind at this rung, for its tooltip. */
    caps: string;
    recoveryBasis: string;
    ceiling: string;
    headroom: string;
    ebitda: string;
    ebitdaDelta: string;
    stepGain: string;
    requiresCapital: boolean;
    currentBottleneck: boolean;
  }[];
};

function oneDecimal(value: number): string {
  return ((Math.round((value + 1e-9) * 10)) / 10).toFixed(1);
}

function signedStph(value: number): string {
  const magnitude = Math.abs(value);
  const formatted = magnitude > 0 && magnitude < 0.1 ? magnitude.toFixed(2) : oneDecimal(magnitude);
  return `${value >= 0 ? '+' : '−'}${formatted} stph`;
}

function ceilingStph(value: number, previousValue?: number): string {
  const sameTenthAsPrevious = previousValue !== undefined && oneDecimal(value) === oneDecimal(previousValue);
  return sameTenthAsPrevious ? `${value.toFixed(2)} stph` : `${oneDecimal(value)} stph`;
}

function signedA(value: number): string {
  return `${value >= 0 ? '+' : '−'}${formatCurrency(Math.abs(value))}`;
}

function tenths(value: number): string {
  const magnitude = Math.abs(value);
  const formatted = magnitude > 0 && magnitude < 0.1 ? magnitude.toFixed(2) : oneDecimal(magnitude);
  return `${value >= 0 ? '+' : '−'}${formatted}`;
}

export function bottleneckLadderProjection(read: BottleneckLadderRead): BottleneckLadderProjection {
  // D18: the first rung sits exactly at today's feed when its ceiling caps it.
  const feedLimited = read.rungs.length > 0 && Math.abs(read.rungs[0]!.ceilingStph - read.currentThroughputStph) < 1e-9 && read.rungs[0]!.currentBottleneck;
  const capitalRungs = read.rungs.filter((rung) => rung.requiresCapital).length;
  return {
    currentRate: `${oneDecimal(read.currentThroughputStph)} stph current`,
    recoveryFloor: `${oneDecimal(read.recoveryFloorStph)} stph recovery floor`,
    feedLimited,
    rungs: read.rungs.map((rung, index) => {
      const previous = index === 0
        ? { name: 'Current', ceilingStph: read.currentThroughputStph, ebitdaA: read.currentEbitdaA }
        : read.rungs[index - 1]!;
      const limiting = feedLimited && index === 0;
      const stepA = rung.ebitdaA - previous.ebitdaA;
      return {
        id: rung.id,
        name: rung.name,
        // D22: each rung names its own constraint once, so a jump in price reads
        // as a change of bottleneck; the feed-limiting rung says so instead of
        // repeating the plant-wide bottleneck on every rung.
        label: limiting ? `Feed limited · ${rung.name}` : rung.name,
        pill: limiting ? null : !rung.requiresCapital ? 'Operating' : capitalRungs > 1 && read.rungs.length > 2 ? 'Capital' : 'Capital required',
        rateLine: limiting
          ? `${oneDecimal(rung.ceilingStph)} stph · headroom 0`
          : `${oneDecimal(rung.ceilingStph)} stph · ${tenths(rung.ceilingStph - previous.ceilingStph)}`,
        money: limiting ? `${formatCurrency(rung.ebitdaA)}/day` : `${signedA(stepA)}/day`,
        moneyTone: limiting ? 'neutral' : stepA > 0.005 ? 'ok' : stepA < -0.005 ? 'bad' : 'neutral',
        caps: rung.cappedRecoveryMetals.map(recoveryCapPhrase).join(' · '),
        recoveryBasis: read.rungs.find(row => row.id === rung.recoveryBasis)?.name ?? rung.recoveryBasis,
        ceiling: ceilingStph(rung.ceilingStph, index > 0 ? previous.ceilingStph : undefined),
        headroom: `${signedStph(rung.headroomFromCurrentStph)} vs ${oneDecimal(read.currentThroughputStph)} stph current`,
        ebitda: `${formatCurrency(rung.ebitdaA)}/day`,
        ebitdaDelta: `${signedA(rung.ebitdaDeltaA)}/day vs today's ${formatCurrency(read.currentEbitdaA)}`,
        stepGain: `Step from ${previous.name} (${ceilingStph(previous.ceilingStph)} · ${formatCurrency(previous.ebitdaA)}/day): ${signedStph(rung.ceilingStph - previous.ceilingStph)} · ${signedA(rung.ebitdaA - previous.ebitdaA)}/day`,
        requiresCapital: rung.requiresCapital,
        currentBottleneck: rung.currentBottleneck,
      };
    }),
  };
}
